import { APIError } from "./APIError";
import { triggerLogout } from "./authLogout";
import { WebSocketManager, type MessageHandlers, type ConnectionState } from "../utils/WebSocketManager";

export type Result<T> = readonly [T, null] | readonly [null, APIError];

export type PlatformClient = {
    get<T>(path: string, params?: Record<string, unknown>): Promise<Result<T>>;
    post<T>(path: string, body?: unknown): Promise<Result<T>>;
    put<T>(path: string, body?: unknown): Promise<Result<T>>;
    patch<T>(path: string, body?: unknown): Promise<Result<T>>;
    delete<T = void>(path: string, body?: unknown): Promise<Result<T>>;
    upload<T>(path: string, formData: FormData): Promise<Result<T>>;
    ws(path: string, handlers: MessageHandlers, onStateChange?: (state: ConnectionState) => void): WebSocketManager;
};

function buildURL(base: string, path: string, params?: Record<string, unknown>): string {
    const url = new URL(path, base.endsWith("/") ? base : base + "/");
    if (params) {
        for (const [k, v] of Object.entries(params)) {
            if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
        }
    }
    return url.toString();
}

async function request<T>(
    baseURL: string,
    method: string,
    path: string,
    body?: unknown,
    params?: Record<string, unknown>,
): Promise<Result<T>> {
    const isFormData = body instanceof FormData;
    const headers: Record<string, string> = {
        "Aurora-Time-Zone": localStorage.getItem("auroraTimeZone") ?? "UTC",
    };
    if (body !== undefined && !isFormData) headers["Content-Type"] = "application/json";

    let res: Response;
    try {
        res = await fetch(buildURL(baseURL, path, params), {
            method,
            headers,
            body: isFormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
        });
    } catch (e) {
        return [null, new APIError(e instanceof Error ? e.message : "Network error")];
    }

    if (!res.ok) {
        const traceID = res.headers.get("Trace-ID") ?? undefined;
        let data: unknown;
        try { data = await res.json(); } catch { /* non-JSON error body */ }

        const d = data as Record<string, unknown> | undefined;
        if (res.status === 401 && d?.message === "token has been revoked") {
            void triggerLogout();
        }

        const msg = d?.error as string | undefined ?? (res.statusText || `Request failed (${res.status})`);
        return [null, new APIError(msg, res.status, traceID, data)];
    }

    if (res.status === 204 || res.headers.get("Content-Length") === "0") {
        return [undefined as unknown as T, null];
    }

    try {
        return [await res.json() as T, null];
    } catch {
        return [null, new APIError("Failed to parse response", res.status)];
    }
}

export function createPlatformClient(baseURL: string): PlatformClient {
    const r = <T,>(method: string, path: string, body?: unknown, params?: Record<string, unknown>) =>
        request<T>(baseURL, method, path, body, params);

    const wsBase = baseURL.replace(/^https:\/\//, "wss://").replace(/^http:\/\//, "ws://").replace(/\/$/, "");

    return {
        get:    (path, params) => r("GET", path, undefined, params),
        post:   (path, body)   => r("POST", path, body),
        put:    (path, body)   => r("PUT", path, body),
        patch:  (path, body)   => r("PATCH", path, body),
        delete: (path, body)   => r("DELETE", path, body),
        upload: (path, formData) => r("POST", path, formData),
        ws: (path, handlers, onStateChange) => {
            const tokenFetcher = async (): Promise<string> => {
                const [data, err] = await r<{ token: string }>("GET", "/api/ws/token");
                if (err) throw new Error(err.message);
                return data.token;
            };
            return new WebSocketManager(wsBase + path, tokenFetcher, handlers, onStateChange);
        },
    };
}
